import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  CircleSlash,
  Clock,
  Download,
  FileText,
  Hourglass,
  Printer,
  RefreshCw,
  Send,
  Tags,
  WifiOff,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { cn, numero } from '@/lib/utils';
import { useParamUrl, useAtualizarParams } from '@/hooks/useUrlEstado';
import { useContexto } from '@/hooks/useContexto';
import { abrirRomaneioNoEdge } from '@/lib/imprimirPapel';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Cartao, CorpoCartao, Esqueleto, Kpi } from '@/componentes/ui/primitivos';
import {
  CSS_ROMANEIO,
  RomaneioPapel,
  type LinhaRomaneio,
} from '@/componentes/romaneio/RomaneioPapel';

/**
 * Etiquetas de marketplace — hoje, Mercado Livre.
 *
 * A tela existe por causa de uma pergunta que hoje vira chamado: "mandei
 * imprimir o romaneio com 10 pedidos e só saíram 8 etiquetas, cadê as outras
 * duas?". Ninguém na expedição tem como consultar o ML, então a operação abre
 * suporte e o suporte olha o dado que poderia estar aqui.
 *
 * Por isso a grade não é uma lista de pedidos: é uma lista de MOTIVOS. Cada
 * linha diz, em português, se a etiqueta sai, se ainda não saiu e por quê, ou
 * se não vai sair nunca — e nesse caso o operador segue com as 8 sem esperar
 * ninguém.
 *
 * O status vem do ML AO VIVO, não da tabela de resumo: aquela é uma foto
 * recarregada uma vez por ciclo, e um pedido cancelado depois do ciclo aparece
 * lá como pago. Quando o ML não responde, a linha diz que está mostrando a
 * foto, em vez de fingir que o dado é fresco.
 */

type SaidaEtiqueta = 'sai' | 'ainda_nao' | 'nunca';
type TomStatus = 'ok' | 'espera' | 'problema' | 'neutro';

interface StatusPedido {
  codigo: string;
  conhecido: boolean;
  rotulo: string;
  detalhe: string;
  tom: TomStatus;
  etiqueta: SaidaEtiqueta;
}

interface PedidoEtiqueta {
  televenda: number | null;
  pedidoMarketplace: string;
  shipmentId: string | null;
  /**
   * Os pacotes do TikTok. Vazio nos outros canais.
   *
   * O ML imprime por ENVIO e o TikTok por PACOTE; a etiqueta existe quando um
   * dos dois existe, e é por isso que o botão não pode olhar só o shipment.
   */
  pacotes?: string[];
  rastreio?: string | null;
  lojaId: number | null;
  /** De qual marketplace o pedido é — decide o que a linha pode dizer. */
  canal: Canal;
  romaneio: number;
  statusProcfit: string | null;
  status: StatusPedido;
  /** `false` = a linha está mostrando a foto do banco, não o ML. */
  aoVivo: boolean;
  erroMl: string | null;
  /** Prazo de despacho no ML (`expected_date` do SLA). */
  slaEm: string | null;
  slaStatus: string | null;
  /** Até quando o ML segura a etiqueta, quando o envio está retido. */
  retidoAte: string | null;
  agendamentoFoto: string | null;
  fotoDe: string | null;
}

interface Resposta {
  romaneio: number;
  pedidos: PedidoEtiqueta[];
  resumo: {
    total: number;
    saem: number;
    aguardando: number;
    naoSaem: number;
    semRespostaDoMl: number;
    /** Pedidos de canais que o WMS ainda não consulta. */
    deOutrosCanais: number;
    contas: number;
  };
}

/**
 * O romaneio interno, como a tela de Romaneios já o conhece.
 *
 * Só os campos que o papel A4 consome — esta tela não repete a tela de
 * Romaneios, só oferece o atalho de imprimir o mesmo papel sem sair daqui.
 */
interface RomaneioDetalhe {
  cabecalho: {
    romaneio: number;
    data: string | null;
    transportadoraNome: string | null;
  };
  linhas: LinhaRomaneio[];
  qtdePedidos: number;
  totalVolumes: number;
  totalPesoGramas: number;
}

/**
 * O nome curto de uma transportadora.
 *
 * O cadastro guarda a razão social inteira — "MERCADO ENVIOS SERVICOS DE
 * LOGISTICA LTDA." ocupa a largura toda da coluna para dizer o que "Mercado
 * Envios" já diz. Espaço gasto ali é espaço tirado do detalhe à direita, que é
 * onde estão os botões de imprimir.
 *
 * Os apelidos conhecidos vêm primeiro; o resto cai na regra genérica de cortar
 * sufixo societário. Assim uma transportadora nova entra legível sem ninguém
 * precisar editar este mapa.
 */
const APELIDOS: { casa: RegExp; nome: string }[] = [
  { casa: /mercado\s*envios|mercado\s*liv/i, nome: 'Mercado Envios' },
  { casa: /tiktok/i, nome: 'TikTok Logistics' },
  { casa: /shopee/i, nome: 'Shopee' },
  { casa: /brasil\s*web/i, nome: 'Brasil Web' },
  // Os limites de palavra importam: sem eles "mma" casaria dentro de outro nome.
  { casa: /\bm\.?\s?m\.?\s?a\.?\b/i, nome: 'MMA' },
  { casa: /tex\s*courier/i, nome: 'TEX Courier' },
  { casa: /total\s*express/i, nome: 'Total Express' },
  { casa: /favorita/i, nome: 'Favorita' },
  { casa: /atual\s*(cargas)?/i, nome: 'Atual Cargas' },
  { casa: /redivix/i, nome: 'Redivix' },
  { casa: /j&t|jet\s*express/i, nome: 'J&T Express' },
  { casa: /correios/i, nome: 'Correios' },
];

function nomeCurto(transportadora: string | null): string {
  const bruto = (transportadora ?? '').trim();
  if (bruto === '') return 'sem transportadora';

  const apelido = APELIDOS.find((a) => a.casa.test(bruto));
  if (apelido) return apelido.nome;

  /*
   * Os limites de palavra (\b) NÃO são opcionais aqui: sem eles, "me" seria
   * removido do meio de "MERCADO" e a transportadora viraria "Rcado".
   */
  const limpo = bruto
    .replace(/\b(ltda|s\.?\s?a\.?|me|epp|eireli|eirelli|cia|s\/a)\b\.?/gi, '')
    .replace(
      /\b(transportes?|transportadora|logistica|log[íi]stica|servi[çc]os?|de|e|do|da)\b/gi,
      ' ',
    )
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const palavras = (limpo || bruto).split(' ').slice(0, 2);
  return palavras
    .map((p) => (p.length > 3 ? p[0] + p.slice(1).toLowerCase() : p.toUpperCase()))
    .join(' ');
}

/** Uma linha da lista de romaneios da esquerda. */
interface RomaneioDaLista {
  romaneio: number;
  pedidos: number;
  volumes: number;
  data: string | null;
  transportadora: string | null;
  canal: Canal;
}

type Canal = 'meli' | 'shopee' | 'tiktok' | 'outro';

interface RespostaLista {
  empresa: number;
  canal: Canal | null;
  romaneios: RomaneioDaLista[];
}

/**
 * Os marketplaces, na ordem em que aparecem nas abas.
 *
 * A ordem é a do volume real da operação, não alfabética: o Mercado Livre é
 * quase tudo, e uma aba que exige procurar é uma aba mal colocada.
 */
const CANAIS: { id: Canal; rotulo: string; curto: string }[] = [
  { id: 'meli', rotulo: 'Mercado Livre', curto: 'ML' },
  { id: 'shopee', rotulo: 'Shopee', curto: 'Shopee' },
  { id: 'tiktok', rotulo: 'TikTok', curto: 'TikTok' },
  { id: 'outro', rotulo: 'Outros', curto: 'Outros' },
];

const CLASSE_TOM: Record<TomStatus, string> = {
  ok: 'bg-status-livreFundo text-status-livre',
  espera: 'bg-status-atencaoFundo text-status-atencao',
  problema: 'bg-status-divergenciaFundo text-status-divergencia',
  neutro: 'bg-superficie2 text-textoFraco',
};

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function soData(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function Etiquetas(): JSX.Element {
  const { empresa } = useContexto();
  const [selecionado] = useParamUrl('romaneio');
  const [realcada] = useParamUrl('televenda');
  const romaneio = Number(selecionado) || null;
  const televendaRealcada = Number(realcada) || null;

  /*
   * Os dois parâmetros mudam JUNTOS, numa escrita só.
   *
   * `useParamUrl` tem um `setSearchParams` por chave: chamar dois no mesmo
   * handler é uma corrida em que o segundo lê a URL antes de o primeiro
   * aplicar e sobrescreve o que ele escreveu. Aqui isso se manifestava como
   * "cliquei no romaneio e o detalhe não abriu" — o `televenda` apagava o
   * `romaneio` que tinha acabado de ser gravado. É a mesma armadilha que já
   * tinha impedido o clique no corredor de abrir o rack, e `useAtualizarParams`
   * existe exatamente para ela.
   */
  const atualizarUrl = useAtualizarParams();

  /*
   * Os últimos romaneios COM pedido de marketplace.
   *
   * Só banco, sem ida ao ML: a lista é para escolher, e consultar o ML de 50
   * romaneios de uma vez custaria centenas de chamadas para uma informação
   * que só interessa no romaneio que for aberto.
   */
  const [canal, definirCanal] = React.useState<Canal | 'todos'>('todos');

  const lista = useQuery<RespostaLista>({
    queryKey: ['etiquetas', 'romaneios', empresa, canal],
    queryFn: () =>
      api.get<RespostaLista>('/api/etiquetas/romaneios', {
        empresa,
        limite: 50,
        canal: canal === 'todos' ? undefined : canal,
      }),
    enabled: empresa !== null,
    staleTime: 60_000,
  });



  const consulta = useQuery<Resposta>({
    queryKey: ['etiquetas', romaneio],
    queryFn: () => api.get<Resposta>(`/api/etiquetas/romaneio/${romaneio}`),
    enabled: romaneio !== null,
    // Sem refetch automático: cada carga custa duas chamadas ao ML por pedido.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  /*
   * O romaneio interno, para o atalho de impressão do RTM.
   *
   * Consulta separada e de propósito: ela é barata (~150 ms, só banco) e não
   * depende do Mercado Livre. Assim o papel A4 continua saindo mesmo quando o
   * ML está fora do ar — que é justamente o dia em que alguém mais precisa do
   * papel na mão.
   */
  const interno = useQuery<RomaneioDetalhe>({
    queryKey: ['romaneio', romaneio],
    queryFn: () => api.get<RomaneioDetalhe>(`/api/romaneios/${romaneio}`),
    enabled: romaneio !== null,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <CabecalhoPagina
        sobrelinha="Operação"
        titulo="Etiquetas de marketplace"
        descricao="Digite o romaneio e veja, pedido a pedido, se a etiqueta do Mercado Livre sai — e por que não, quando não sai."
        icone={<Tags className="h-5 w-5" />}
        acoes={
          <>
            <BuscaNumero
              inicial={selecionado}
              aoAchar={(rom, tv) =>
                atualizarUrl({
                  romaneio: String(rom),
                  televenda: tv === null ? null : String(tv),
                })
              }
            />
            {romaneio !== null ? (
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                onClick={() => void consulta.refetch()}
                carregando={consulta.isFetching}
              >
                <RefreshCw className="h-4 w-4" />
                Consultar o ML de novo
              </Botao>
            ) : null}
          </>
        }
      />

      {/*
        Duas colunas que rolam SOZINHAS — a página não rola.

        A primeira tentativa foi `sticky` no cartão da lista, e não resolveu: o
        scroll desta aplicação é do `<main>` (AppShell), não do documento, e
        uma altura em `100vh` não corresponde à área visível dele. O resultado
        era o defeito original — rolar até o fim da lista e perder o detalhe de
        vista, tendo que voltar ao topo.

        Aqui o container tem altura fixa e cada painel tem o próprio
        `overflow-y-auto`. Rolar a lista não mexe no detalhe, e vice-versa:
        nunca se perde um dos dois. Abaixo de `lg` a grade vira uma coluna e o
        empilhamento normal volta, porque em tela estreita dois painéis com
        rolagem própria só brigam entre si.
      */}
      <div className="grid items-start gap-3 lg:h-[calc(100vh-190px)] lg:min-h-[420px] lg:grid-cols-[258px_minmax(0,1fr)] lg:items-stretch">
        <Cartao className="nao-imprimir flex flex-col lg:min-h-0 lg:overflow-hidden">
          <div className="space-y-2 border-b border-borda px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-bold">Últimos romaneios</span>
              <span className="ml-auto text-[11px] text-textoFraco">
                {lista.data ? `${numero(lista.data.romaneios.length)} na lista` : ''}
              </span>
            </div>

            {/*
              Abas fixas, sem contagem.

              Mostrar "Mercado Livre 2.330" exigia um COUNT DISTINCT sobre a
              janela toda a cada carga da tela, e o número não muda decisão
              nenhuma: quem abre a tela quer o romaneio de agora. Cada aba
              busca os seus 50 e pronto.
            */}
            {/*
              Abas curtas porque a coluna é estreita: o selo já diz qual é o
              canal, então o rótulo pode ser o apelido ("ML") em vez do nome
              inteiro. O nome completo fica no `title`.
            */}
            <div className="flex flex-wrap gap-1">
              <AbaCanal
                rotulo="Todos"
                titulo="Todos os marketplaces"
                ativa={canal === 'todos'}
                aoEscolher={() => definirCanal('todos')}
              />
              {CANAIS.map((c) => (
                <AbaCanal
                  key={c.id}
                  canal={c.id}
                  rotulo={c.curto}
                  titulo={c.rotulo}
                  ativa={canal === c.id}
                  aoEscolher={() => definirCanal(c.id)}
                />
              ))}
            </div>
          </div>

          <CorpoCartao className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {lista.isLoading ? (
              <Esqueleto className="h-72" />
            ) : lista.isError ? (
              <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
            ) : (lista.data?.romaneios.length ?? 0) === 0 ? (
              <EstadoVazio
                icone={<Tags className="h-7 w-7" />}
                titulo="Nenhum romaneio com marketplace"
                detalhe="Nenhum dos romaneios recentes desta empresa carrega pedido de marketplace."
              />
            ) : (
              <ul className="divide-y divide-borda">
                {lista.data?.romaneios.map((r) => (
                  <LinhaLista
                    key={r.romaneio}
                    item={r}
                    ativo={romaneio === r.romaneio}
                    aoAbrir={() =>
                      atualizarUrl({ romaneio: String(r.romaneio), televenda: null })
                    }
                  />
                ))}
              </ul>
            )}
          </CorpoCartao>
        </Cartao>

        <div className="lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          {romaneio === null ? (
            <Cartao className="grid min-h-[280px] place-items-center">
              <EstadoVazio
                icone={<Tags className="h-7 w-7" />}
                titulo="Escolha um romaneio"
                detalhe="Clique num romaneio da lista, ou digite o número no campo acima. A consulta vai ao Mercado Livre na hora."
              />
            </Cartao>
          ) : consulta.isLoading ? (
            <div className="space-y-3">
              <Esqueleto className="h-24" />
              <Esqueleto className="h-[320px]" />
              <p className="text-center text-[12.5px] text-textoFraco">
                Consultando o Mercado Livre, um envio de cada vez…
              </p>
            </div>
          ) : consulta.isError ? (
            <Cartao>
              <CorpoCartao>
                <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
                {consulta.error instanceof ErroHttp && consulta.error.status === 404 ? (
                  <p className="mt-2 text-[12.5px] text-textoFraco">
                    Se o número está certo, o romaneio pode ser de outra empresa — troque a empresa
                    no seletor do topo.
                  </p>
                ) : null}
              </CorpoCartao>
            </Cartao>
          ) : consulta.data ? (
            <Detalhe
              dados={consulta.data}
              interno={interno.data ?? null}
              realcada={televendaRealcada}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * O selo de um marketplace.
 *
 * Cada canal ganha a própria marca de cor, porque numa lista misturada a cor é
 * o que separa as cargas sem obrigar a ler. Desenhados aqui, não baixados: um
 * arquivo remoto vira dependência de rede numa tela que precisa funcionar
 * quando a internet está ruim, e as logomarcas são marca registrada — estes
 * são identificadores funcionais, e trocar por assets oficiais é uma linha.
 */
function SeloCanal({ canal, className }: { canal: Canal; className?: string }): JSX.Element {
  if (canal === 'meli') return <SeloMercadoLivre className={className ?? ''} />;

  const cores: Record<Exclude<Canal, 'meli'>, { fundo: string; texto: string; sigla: string }> = {
    shopee: { fundo: '#EE4D2D', texto: '#FFFFFF', sigla: 'S' },
    tiktok: { fundo: '#111111', texto: '#FFFFFF', sigla: 'T' },
    outro: { fundo: '#94A3B8', texto: '#FFFFFF', sigla: '?' },
  };
  const { fundo, texto, sigla } = cores[canal];

  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center', className)}
      title={canal === 'shopee' ? 'Shopee' : canal === 'tiktok' ? 'TikTok' : 'Outro marketplace'}
      aria-label={canal}
    >
      <svg viewBox="0 0 24 24" className="h-full w-full" role="img" aria-hidden>
        <rect x="1" y="1" width="22" height="22" rx="6" fill={fundo} />
        <text
          x="12"
          y="16.5"
          textAnchor="middle"
          fontSize="12"
          fontWeight="700"
          fill={texto}
          fontFamily="system-ui, sans-serif"
        >
          {sigla}
        </text>
      </svg>
    </span>
  );
}

/** Uma aba de marketplace, com quantos romaneios ela tem. */
function AbaCanal({
  canal,
  rotulo,
  titulo,
  ativa,
  aoEscolher,
}: {
  canal?: Canal;
  rotulo: string;
  titulo: string;
  ativa: boolean;
  aoEscolher: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={aoEscolher}
      title={titulo}
      aria-label={titulo}
      aria-pressed={ativa}
      className={cn(
        'flex items-center gap-1 rounded-lg border px-1.5 py-1 text-[11.5px] font-semibold transition-colors',
        ativa
          ? 'border-primaria bg-primaria-suave text-primaria'
          : 'border-borda text-textoFraco hover:bg-superficie2',
      )}
    >
      {canal ? <SeloCanal canal={canal} className="h-4 w-4" /> : null}
      {rotulo}
    </button>
  );
}

/**
 * O selo do Mercado Livre.
 *
 * SVG desenhado aqui, e não a logomarca baixada da internet, por dois motivos:
 * um arquivo remoto vira dependência de rede numa tela que precisa funcionar
 * justamente quando a internet está ruim; e a logomarca oficial é marca
 * registrada — usá-la pediria o arquivo oficial e a permissão de uso.
 *
 * Este é um identificador funcional: o amarelo do ML e o traço do aperto de
 * mão, o suficiente para a linha ser reconhecida de relance. Se você tiver o
 * arquivo oficial, trocar aqui é uma linha.
 *
 * Quando entrar o segundo marketplace, este componente vira um mapa de selos e
 * quem chama passa o canal.
 */
function SeloMercadoLivre({ className }: { className?: string }): JSX.Element {
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center', className)}
      title="Mercado Livre"
      aria-label="Mercado Livre"
    >
      <svg viewBox="0 0 24 24" className="h-full w-full" role="img" aria-hidden>
        <ellipse cx="12" cy="12" rx="11.5" ry="9" fill="#FFE600" />
        <path
          d="M5.6 12.4c1.2-1.9 2.6-3.3 4-3.3.7 0 1.1.4 1.6.9.4.4.8.8 1.3.8.4 0 .7-.2 1-.5.5-.5 1-1.2 1.9-1.2 1.4 0 2.8 1.4 4 3.3"
          fill="none"
          stroke="#2D3277"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M8.4 13.6c.9.8 1.7 1.1 2.3.7.5-.3.5-.9.2-1.3M13.2 13c.7.9 1.5 1.4 2.2 1.1"
          fill="none"
          stroke="#2D3277"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/** Um romaneio na lista da esquerda. */
function LinhaLista({
  item,
  ativo,
  aoAbrir,
}: {
  item: RomaneioDaLista;
  ativo: boolean;
  aoAbrir: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={aoAbrir}
        title={`${nomeCurto(item.transportadora)} · ${numero(item.pedidos)} ${
          item.pedidos === 1 ? 'pedido' : 'pedidos'
        }`}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
          ativo ? 'bg-primaria-suave' : 'hover:bg-superficie2',
        )}
      >
        {/*
          A lista serve para ESCOLHER, não para informar.

          Ela existe ao lado do detalhe, e cada coluna aqui é largura tirada de
          onde estão os botões de imprimir. Ficam só os quatro dados que
          identificam a carga — canal, número, data e volumes; a transportadora
          vai para o `title`, porque quem já filtrou por canal não precisa dela
          escrita em toda linha.
        */}
        <SeloCanal canal={item.canal} className="h-[16px] w-[16px]" />

        <span
          className={cn(
            'tabular shrink-0 font-mono text-[13px] font-bold',
            ativo && 'text-primaria',
          )}
        >
          {item.romaneio}
        </span>

        <span className="tabular ml-auto shrink-0 text-[11px] text-textoFraco">
          {dataHora(item.data)}
        </span>

        <span className="tabular shrink-0 text-[11px] font-semibold text-textoFraco">
          {numero(item.volumes)} vol
        </span>

        {/*
          O selo de cancelados saiu daqui de propósito. Ele obrigava a lista a
          cruzar CADA pedido com ResumoMeliColetas, e era esse cruzamento que
          fazia a tela estourar o limite de 30 s e abrir vazia. O estado do
          marketplace aparece ao ABRIR o romaneio, que é onde se age sobre ele.
        */}
      </button>
    </li>
  );
}

/**
 * Um campo só, para os dois números que a expedição tem na mão.
 *
 * O romaneio é o número do dia a dia; a televenda aparece quando UMA etiqueta
 * molhou e precisa sair de novo. Obrigar a escolher "estou digitando um
 * romaneio" ou "estou digitando uma televenda" é pedir que a pessoa traduza o
 * próprio problema para a linguagem do sistema — então o servidor é que
 * resolve, e quando o número é uma televenda a tela abre o romaneio dela já
 * com a linha destacada.
 */
function BuscaNumero({
  inicial,
  aoAchar,
}: {
  inicial: string;
  aoAchar: (romaneio: number, televenda: number | null) => void;
}): JSX.Element {
  const [valor, definirValor] = React.useState(inicial);
  const [buscando, definirBuscando] = React.useState(false);
  const [erro, definirErro] = React.useState<string | null>(null);

  async function buscar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const n = Number(valor.replace(/\D/g, ''));
    if (!(n > 0)) return;

    definirBuscando(true);
    definirErro(null);
    try {
      const r = await api.get<{ romaneio: number; televenda: number | null }>(
        '/api/etiquetas/buscar',
        { q: n },
      );
      aoAchar(r.romaneio, r.televenda);
    } catch (err) {
      definirErro(
        err instanceof ErroHttp ? err.message : 'Não foi possível buscar. Tente de novo.',
      );
    } finally {
      definirBuscando(false);
    }
  }

  return (
    <form className="flex flex-col gap-0.5" onSubmit={(e) => void buscar(e)}>
      <div className="flex items-center gap-1.5">
        <label htmlFor="num-etiqueta" className="rotulo">
          Romaneio ou televenda
        </label>
        <input
          id="num-etiqueta"
          value={valor}
          onChange={(e) => {
            definirValor(e.target.value.replace(/\D/g, ''));
            definirErro(null);
          }}
          inputMode="numeric"
          placeholder="000000"
          className="tabular min-h-[34px] w-[124px] rounded-lg border border-borda bg-superficie px-2 text-center font-mono text-[14px] font-bold"
        />
        <Botao
          variante="secundaria"
          tamanho="pequeno"
          type="submit"
          disabled={valor === ''}
          carregando={buscando}
        >
          Buscar
        </Botao>
      </div>
      {erro ? (
        <span className="max-w-[280px] text-[11px] text-status-divergencia">{erro}</span>
      ) : null}
    </form>
  );
}

function Detalhe({
  dados,
  interno,
  realcada,
}: {
  dados: Resposta;
  interno: RomaneioDetalhe | null;
  realcada: number | null;
}): JSX.Element {
  const { resumo, pedidos } = dados;
  // 'pdf' | 'zpl' para o lote; `tv-<numero>` para a reimpressão de uma linha.
  const [imprimindo, definirImprimindo] = React.useState<string | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);
  const [verPapel, definirVerPapel] = React.useState(false);
  const [abrindoA4, definirAbrindoA4] = React.useState(false);

  async function imprimirRomaneioA4(): Promise<void> {
    definirErro(null);
    definirAbrindoA4(true);
    try {
      await abrirRomaneioNoEdge(dados.romaneio);
      definirAviso('Romaneio aberto no Edge: escolha a impressora A4 na janela de impressão.');
    } catch (e) {
      definirErro(e instanceof ErroHttp ? e.message : 'Não foi possível abrir o romaneio.');
    } finally {
      definirAbrindoA4(false);
    }
  }
  const cliente = useQueryClient();

  /*
   * O PDF vem pelo fetch, não por `window.open` direto.
   *
   * A rota pode recusar com motivo (nada liberado, token vencido, romaneio com
   * duas contas). Abrindo numa aba nova, esse motivo viraria um JSON cru na
   * cara do operador; aqui a recusa vira frase na própria tela e só o PDF de
   * verdade abre.
   */
  /**
   * Força o envio da NF-e de UM pedido, sem mandar a carga inteira.
   *
   * O caminho automático (imprimir) resolve o romaneio todo, mas quem está
   * conferindo pedido a pedido quer destravar aquele ali e ver o resultado —
   * sem gerar o PDF dos outros doze.
   */
  async function enviarXml(televenda: number): Promise<void> {
    definirImprimindo(`xml-${televenda}`);
    definirErro(null);
    definirAviso(null);
    try {
      const r = await api.post<{ liberou: boolean; mensagem: string }>(
        `/api/etiquetas/romaneio/${dados.romaneio}/xml`,
        { televenda },
      );
      definirAviso(
        r.liberou
          ? `NF-e enviada ao Mercado Livre e a etiqueta do pedido ${televenda} foi liberada.`
          : `NF-e do pedido ${televenda} enviada ao Mercado Livre, mas a etiqueta ainda não liberou. O ML às vezes demora — consulte de novo em instantes.`,
      );
      await cliente.invalidateQueries({ queryKey: ['etiquetas', dados.romaneio] });
    } catch (e) {
      definirErro(e instanceof Error ? e.message : 'Não foi possível enviar a NF-e.');
    } finally {
      definirImprimindo(null);
    }
  }

  /**
   * Envia a NF-e de todos os pedidos presos na nota, numa chamada só.
   *
   * Era um POST por pedido, em série — e cada um esperava o ML por até 12 s,
   * o que dava ~51 s medidos para três pedidos. O servidor agora posta todos
   * em paralelo e espera UMA vez; os erros continuam sendo por pedido, então
   * um que falha não derruba os outros.
   */
  async function enviarXmlDeTodos(): Promise<void> {
    definirImprimindo('xml-lote');
    definirErro(null);
    definirAviso(null);

    const televendas = resolvemNoClique
      .map((p) => p.televenda)
      .filter((t): t is number => t !== null);

    let liberados = 0;
    let falhas = 0;

    try {
      const r = await api.post<{ liberados: number; falhas: number }>(
        `/api/etiquetas/romaneio/${dados.romaneio}/xml`,
        { televendas },
      );
      liberados = r.liberados;
      falhas = r.falhas;
    } catch {
      falhas = televendas.length;
    }

    await cliente.invalidateQueries({ queryKey: ['etiquetas', dados.romaneio] });
    definirImprimindo(null);

    if (falhas > 0 && liberados === 0) {
      definirErro(`Nenhuma NF-e pôde ser enviada (${falhas} ${falhas === 1 ? 'falha' : 'falhas'}).`);
      return;
    }
    definirAviso(
      liberados > 0
        ? `${numero(liberados)} ${liberados === 1 ? 'etiqueta foi liberada' : 'etiquetas foram liberadas'} pelo Mercado Livre.` +
            (falhas > 0 ? ` ${falhas} não deu certo.` : '')
        : 'NF-es enviadas ao Mercado Livre, mas nenhuma etiqueta liberou ainda. O ML às vezes demora — consulte de novo em instantes.',
    );
  }

  async function imprimir(formato: 'pdf' | 'zpl', televenda?: number): Promise<void> {
    definirImprimindo(televenda === undefined ? formato : `tv-${televenda}`);
    definirErro(null);
    definirAviso(null);
    try {
      const resposta = await fetch(
        `/api/etiquetas/romaneio/${dados.romaneio}/pdf?formato=${formato}` +
          (televenda === undefined ? '' : `&televenda=${televenda}`),
        { credentials: 'include' },
      );

      if (!resposta.ok) {
        const corpo = (await resposta.json().catch(() => null)) as { mensagem?: string } | null;
        definirErro(corpo?.mensagem ?? 'Não foi possível gerar as etiquetas.');
        return;
      }

      /*
       * O que o servidor fez com os `invoice_pending`, por header.
       *
       * O corpo é o PDF, então não cabe JSON junto. A tela precisa disso para
       * dizer "2 etiquetas foram liberadas agora" — senão o operador vê o PDF
       * vir com mais etiquetas do que a grade mostrava e não entende por quê.
       */
      const enviados = Number(resposta.headers.get('X-Xml-Enviados') ?? 0);
      const liberados = Number(resposta.headers.get('X-Xml-Liberados') ?? 0);
      if (enviados > 0) {
        definirAviso(
          liberados > 0
            ? `${liberados} ${liberados === 1 ? 'etiqueta estava' : 'etiquetas estavam'} presa${liberados === 1 ? '' : 's'} esperando a nota fiscal. O XML foi enviado ao Mercado Livre agora e ${liberados === 1 ? 'ela entrou' : 'elas entraram'} na impressão.`
            : `O XML de ${enviados} ${enviados === 1 ? 'pedido foi enviado' : 'pedidos foi enviado'} ao Mercado Livre, mas a etiqueta ainda não liberou. Tente de novo em alguns instantes.`,
        );
      }

      const blob = await resposta.blob();
      const url = URL.createObjectURL(blob);

      if (formato === 'pdf') {
        window.open(url, '_blank', 'noopener');
      } else {
        // ZPL vem do ML dentro de um ZIP: não há o que "abrir", tem que baixar.
        const link = document.createElement('a');
        link.href = url;
        link.download =
          televenda === undefined
            ? `etiquetas-romaneio-${dados.romaneio}.zip`
            : `etiqueta-televenda-${televenda}.zip`;
        link.click();
      }

      // Revoga depois de dar tempo de a aba (ou o download) assumir o blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      definirErro('Não foi possível falar com o servidor.');
    } finally {
      definirImprimindo(null);
    }
  }

  const semResposta = resumo.semRespostaDoMl > 0;

  /*
   * O que está impedindo etiqueta de sair, agrupado pelo MOTIVO que o operador
   * lê — não pelo código do ML. Dois pedidos "Aguardando nota fiscal" viram
   * uma linha "2 × Aguardando nota fiscal", porque a ação é a mesma para os
   * dois e repetir a frase só faz a lista crescer.
   */
  const bloqueados = pedidos.filter((p) => p.status.etiqueta !== 'sai');

  /** Carga 100% Mercado Livre — decide o vocabulário da grade. */
  const soDoMeli = pedidos.every((p) => p.canal === 'meli');

  /*
   * Nem toda pendência é igual, e a diferença muda o que a pessoa faz agora.
   *
   * "Aguardando nota fiscal" o WMS resolve sozinho no clique de imprimir: ele
   * posta o XML e o ML libera. Dizer só "3 pendências" faz o operador ir
   * atrás de algo que já está resolvido — foi exatamente essa a confusão. As
   * outras (cancelado, retido) não têm o que fazer aqui, e por isso aparecem
   * separadas.
   */
  const resolvemNoClique = bloqueados.filter((p) => p.status.codigo === 'invoice_pending');
  /*
   * Pedido de outro canal não é "pendência": não há nada errado com ele, o WMS
   * é que ainda não fala com aquele marketplace. Contá-lo como problema faria
   * um romaneio saudável de Shopee parecer quebrado.
   */
  const deOutroCanal = bloqueados.filter((p) => p.canal !== 'meli' && p.canal !== 'tiktok');
  const naoResolvem = bloqueados.filter(
    (p) => p.status.codigo !== 'invoice_pending' && p.canal === 'meli',
  );

  const motivos = [
    ...naoResolvem.reduce((mapa, p) => {
      mapa.set(p.status.rotulo, (mapa.get(p.status.rotulo) ?? 0) + 1);
      return mapa;
    }, new Map<string, number>()),
  ].sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-4">
        <Kpi
          rotulo="Etiquetas que saem"
          valor={numero(resumo.saem)}
          /*
            O denominador diz DE QUE universo, quando ele nao e a carga inteira.

            "0 de 2 pedidos" num romaneio de dois pedidos TikTok sugere que o
            WMS deveria ter tirado duas etiquetas e nao tirou nenhuma. São
            zero de zero: as duas saem pelo painel do canal.
          */
          nota={
            resumo.deOutrosCanais > 0
              ? `de ${numero(resumo.total - resumo.deOutrosCanais)} em canal integrado`
              : `de ${numero(resumo.total)} pedidos`
          }
          tom="ok"
          icone={<Printer className="h-4 w-4" />}
        />
        <Kpi
          rotulo="Ainda não liberadas"
          valor={numero(resumo.aguardando)}
          nota={resumo.aguardando > 0 ? 'o ML ainda não soltou' : 'nada em espera'}
          tom={resumo.aguardando > 0 ? 'alerta' : 'neutro'}
          icone={<Hourglass className="h-4 w-4" />}
        />
        <Kpi
          rotulo="Não vão sair"
          valor={numero(resumo.naoSaem)}
          nota={resumo.naoSaem > 0 ? 'cancelado ou bloqueado' : 'nenhum cancelado'}
          tom={resumo.naoSaem > 0 ? 'erro' : 'neutro'}
          icone={<CircleSlash className="h-4 w-4" />}
        />
        {/*
          Quando a carga tem pedido de outro canal, o quarto indicador fala
          DISSO — não de "sem resposta do ML". Um romaneio só de Shopee
          marcando "1 sem resposta do ML" culpa o sistema errado.
        */}
        {resumo.deOutrosCanais > 0 ? (
          <Kpi
            rotulo="De outros canais"
            valor={numero(resumo.deOutrosCanais)}
            nota="Shopee: fora do WMS por enquanto"
            tom="neutro"
            icone={<Tags className="h-4 w-4" />}
          />
        ) : (
          <Kpi
            rotulo="Sem resposta do ML"
            valor={numero(resumo.semRespostaDoMl)}
            nota={semResposta ? 'mostrando a foto do banco' : 'todos consultados ao vivo'}
            tom={semResposta ? 'alerta' : 'neutro'}
            icone={<WifiOff className="h-4 w-4" />}
          />
        )}
      </div>

      <Cartao>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda px-4 py-3">
          <div>
            <p className="rotulo">Romaneio</p>
            <h2 className="tabular font-mono text-[22px] font-bold leading-tight">
              {dados.romaneio}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/*
              O romaneio interno, sem sair daqui.
              São dois papéis diferentes e é isso que os botões dizem: as
              ETIQUETAS vêm do Mercado Livre e vão na caixa; o RTM é o nosso
              A4, o que o motorista assina. Quem está na expedição imprime os
              dois no mesmo momento, e antes tinha que trocar de tela para isso.
            */}
            <Botao
              variante="fantasma"
              onClick={() => definirVerPapel((v) => !v)}
              disabled={interno === null || interno.linhas.length === 0}
            >
              {verPapel ? 'Esconder o romaneio' : 'Ver o romaneio'}
            </Botao>
            <Botao
              variante="secundaria"
              onClick={() => void imprimirRomaneioA4()}
              carregando={abrindoA4}
              disabled={interno === null || interno.linhas.length === 0}
              title={
                interno === null
                  ? 'Carregando o romaneio interno…'
                  : interno.linhas.length === 0
                    ? 'Este romaneio não tem linha para imprimir no papel A4.'
                    : 'Abre o romaneio no Edge, com a prévia e a escolha da impressora A4 — o papel que o motorista assina.'
              }
            >
              <FileText className="h-4 w-4" />
              Imprimir Romaneio
            </Botao>

            <Botao
              variante="primaria"
              onClick={() => void imprimir('pdf')}
              carregando={imprimindo === 'pdf'}
              disabled={resumo.saem === 0 || imprimindo !== null}
            >
              <Printer className="h-4 w-4" />
              Imprimir {numero(resumo.saem)}{' '}
              {resumo.saem === 1 ? 'etiqueta' : 'etiquetas'}
            </Botao>

            {/*
              ZPL para quem imprime em Zebra.
              Fica como ação secundária porque o caminho comum é o PDF; o ZPL
              vem do ML dentro de um ZIP, então BAIXA em vez de abrir — e o
              rótulo diz isso, para ninguém ficar esperando uma aba abrir.
            */}
            <Botao
              variante="fantasma"
              onClick={() => void imprimir('zpl')}
              carregando={imprimindo === 'zpl'}
              disabled={resumo.saem === 0 || imprimindo !== null}
              title="Baixa as etiquetas em ZPL (arquivo .zip), para impressora Zebra."
            >
              <Download className="h-4 w-4" />
              ZPL
            </Botao>
          </div>
        </div>

        <CorpoCartao className="space-y-3">
          {erro ? (
            <p className="flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {erro}
            </p>
          ) : null}

          {aviso ? (
            <p className="flex items-start gap-2 rounded-lg bg-status-livreFundo px-3 py-2 text-[12.5px] text-status-livre">
              <Check className="mt-0.5 h-4 w-4 shrink-0" />
              {aviso}
            </p>
          ) : null}

          {/*
            O aviso que evita o chamado.
            Aparece só quando há diferença entre o que o romaneio carrega e o
            que vai sair impresso — que é exatamente o momento em que alguém
            pegaria o telefone.
          */}
          {/*
            O aviso que evita o chamado.

            Não é um texto discreto de rodapé de propósito: quem imprime um
            romaneio incompleto sem ver isso liga para o suporte. A faixa é
            larga, vermelha e traz a conta ("10 pedidos, 8 etiquetas") mais a
            quebra por motivo, porque cada motivo pede uma ação diferente —
            cancelado se tira da carga, aguardando nota se resolve no clique de
            imprimir, retido só espera.
          */}
          {/*
            Duas faixas, porque são duas situações com ações opostas.

            A primeira é boa notícia disfarçada de pendência: "aguardando nota
            fiscal" o próprio botão de imprimir resolve, postando o XML. Contar
            isso junto com cancelado, sob o título "3 pendências", mandava o
            operador atrás de algo que já ia se resolver sozinho.
          */}
          {resolvemNoClique.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-status-atencao/60 bg-status-atencaoFundo px-3 py-2.5 text-status-atencao">
              <p className="flex min-w-0 flex-1 items-start gap-2 text-[12.5px]">
                <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>
                    {numero(resolvemNoClique.length)}{' '}
                    {resolvemNoClique.length === 1 ? 'pedido está' : 'pedidos estão'} aguardando a
                    nota fiscal no Mercado Livre.
                  </strong>{' '}
                  Isto o WMS resolve: ao mandar imprimir, o XML da NF-e é enviado e{' '}
                  {resolvemNoClique.length === 1 ? 'a etiqueta entra' : 'as etiquetas entram'} na
                  impressão. O ML leva alguns segundos para liberar, então a impressão espera por{' '}
                  {resolvemNoClique.length === 1 ? 'ela' : 'elas'}.
                </span>
              </p>

              {/*
                Resolver tudo sem gerar PDF.
                Quem só quer destravar a carga e imprimir depois não precisa
                passar pela impressão para isso.
              */}
              <Botao
                variante="primaria"
                tamanho="pequeno"
                className="shrink-0"
                onClick={() => void enviarXmlDeTodos()}
                carregando={imprimindo === 'xml-lote'}
                disabled={imprimindo !== null && imprimindo !== 'xml-lote'}
              >
                <Send className="h-4 w-4" />
                Enviar {numero(resolvemNoClique.length)}{' '}
                {resolvemNoClique.length === 1 ? 'NF-e' : 'NF-es'} agora
              </Botao>
            </div>
          ) : null}

          {deOutroCanal.length > 0 ? (
            <p className="flex items-start gap-2 rounded-xl border border-borda bg-superficie2 px-3 py-2.5 text-[12.5px]">
              <Tags className="mt-0.5 h-4 w-4 shrink-0 text-textoFraco" />
              <span>
                <strong>
                  {numero(deOutroCanal.length)}{' '}
                  {deOutroCanal.length === 1 ? 'pedido é' : 'pedidos são'} de outro marketplace
                </strong>{' '}
                ({[...new Set(deOutroCanal.map((p) => p.status.rotulo.replace('Pedido ', '')))].join(
                  ', ',
                )}
                ). O WMS ainda não consulta {deOutroCanal.length === 1 ? 'esse canal' : 'esses canais'}
                , então a etiqueta {deOutroCanal.length === 1 ? 'dele' : 'deles'} sai pelo painel do
                próprio marketplace — não é erro do romaneio.
              </span>
            </p>
          ) : null}

          {naoResolvem.length > 0 ? (
            <div className="rounded-xl border-2 border-status-divergencia bg-status-divergenciaFundo px-3 py-2.5">
              <p className="flex items-start gap-2 text-[13px] text-status-divergencia">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-[14px]">
                    {numero(naoResolvem.length)}{' '}
                    {naoResolvem.length === 1
                      ? 'pedido não vai gerar etiqueta'
                      : 'pedidos não vão gerar etiqueta'}
                    .
                  </strong>{' '}
                  São <strong>{numero(resumo.total)} pedidos</strong> na carga e a impressão sai com{' '}
                  <strong>{numero(resumo.saem)}</strong> — não é falha de impressão, e imprimir de
                  novo não muda.
                </span>
              </p>

              <ul className="mt-2 flex flex-wrap gap-1.5">
                {motivos.map(([motivo, quantos]) => (
                  <li
                    key={motivo}
                    className="rounded-md border border-status-divergencia/40 bg-superficie px-2 py-1 text-[11.5px] font-semibold text-status-divergencia"
                  >
                    {numero(quantos)} × {motivo}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {resumo.contas > 1 ? (
            <p className="rounded-lg bg-superficie2 px-3 py-2 text-[12.5px]">
              Este romaneio mistura <strong>{resumo.contas} contas</strong> do Mercado Livre. Cada
              conta gera um PDF próprio, então a impressão precisa ser feita por conta.
            </p>
          ) : null}

          {pedidos.length === 0 ? (
            <EstadoVazio
              titulo="Nenhum pedido de marketplace neste romaneio"
              detalhe="O romaneio existe, mas não carrega pedido do Mercado Livre."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-[12.5px]">
                {/*
                  O CABEÇALHO SEGUE O CANAL DA CARGA.

                  Estava fixo em "Pedido no ML / Situação no Mercado Livre /
                  Prazo ML", e o romaneio 769101 — dois pedidos TikTok, nenhum
                  do ML — exibia três colunas falando de um marketplace que não
                  tem nada a ver com aquela carga. É o mesmo erro do contador
                  "o ML ainda não soltou": vocabulário do ML numa linha que não
                  é dele. Havendo pedido de outro canal, o título vira neutro.
                */}
                <thead>
                  <tr className="border-b border-borda text-left">
                    <th className="rotulo py-1.5 pr-2">
                      {soDoMeli ? 'Pedido no ML' : 'Pedido no marketplace'}
                    </th>
                    <th className="rotulo py-1.5 pr-2">Televenda</th>
                    <th className="rotulo py-1.5 pr-2">
                      {soDoMeli ? 'Situação no Mercado Livre' : 'Situação no canal'}
                    </th>
                    <th className="rotulo py-1.5 pr-2">{soDoMeli ? 'Prazo ML' : 'Prazo'}</th>
                    <th className="rotulo py-1.5 pr-2">No ERP</th>
                    <th className="rotulo py-1.5 text-right">Reimprimir</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.map((p) => (
                    <Linha
                      key={`${p.pedidoMarketplace}-${p.shipmentId ?? 's'}`}
                      pedido={p}
                      realcada={realcada !== null && p.televenda === realcada}
                      ocupado={imprimindo !== null}
                      reimprimindo={imprimindo === `tv-${p.televenda ?? 0}`}
                      aoReimprimir={(fmt) =>
                        p.televenda === null ? undefined : void imprimir(fmt, p.televenda)
                      }
                      enviandoXml={imprimindo === `xml-${p.televenda ?? 0}`}
                      aoEnviarXml={() =>
                        p.televenda === null ? undefined : void enviarXml(p.televenda)
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pedidos.some((p) => p.canal === 'meli') && pedidos[0]?.fotoDe ? (
            <p className="text-[11.5px] text-textoFraco">
              Situação consultada no Mercado Livre agora. A foto do banco, usada quando o ML não
              responde, é de {dataHora(pedidos[0].fotoDe)}.
            </p>
          ) : null}
        </CorpoCartao>
      </Cartao>

      {/*
        A prévia do papel, quando pedida. A impressão não sai daqui: sai da
        página `/romaneio-a4`, no Edge (ver `abrirRomaneioNoEdge`).
      */}
      {verPapel && interno ? (
        <div className="cartao overflow-x-auto p-4">
          <style>{CSS_ROMANEIO}</style>
          <p className="rotulo mb-2">Prévia do romaneio (A4)</p>
          <div style={{ width: '210mm', maxWidth: '100%' }}>
            <RomaneioPapel
              romaneio={interno.cabecalho.romaneio}
              data={interno.cabecalho.data}
              transportadora={interno.cabecalho.transportadoraNome}
              linhas={interno.linhas}
              qtdePedidos={interno.qtdePedidos}
              totalVolumes={interno.totalVolumes}
              totalPesoGramas={interno.totalPesoGramas}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Linha({
  pedido,
  realcada,
  ocupado,
  reimprimindo,
  aoReimprimir,
  enviandoXml,
  aoEnviarXml,
}: {
  pedido: PedidoEtiqueta;
  realcada: boolean;
  ocupado: boolean;
  reimprimindo: boolean;
  aoReimprimir: (formato: 'pdf' | 'zpl') => void;
  enviandoXml: boolean;
  aoEnviarXml: () => void;
}): JSX.Element {
  const { status } = pedido;
  const ref = React.useRef<HTMLTableRowElement>(null);

  /*
   * Quem chegou aqui buscando UMA televenda precisa vê-la sem procurar.
   *
   * O romaneio pode ter 20 linhas e a busca por televenda existe justamente
   * para o caso urgente (a etiqueta molhou). Abrir a tela e deixar a pessoa
   * caçando o número na lista desfaria o ganho da busca.
   */
  React.useEffect(() => {
    if (realcada) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [realcada]);

  return (
    <tr
      ref={ref}
      className={cn(
        'border-b border-borda/60 align-top',
        status.etiqueta === 'nunca' && 'bg-status-divergenciaFundo/40',
        realcada && 'bg-primaria-suave ring-1 ring-inset ring-primaria',
      )}
    >
      <td className="py-2 pr-2">
        <span className="flex items-center gap-1.5">
          <SeloCanal canal={pedido.canal} className="h-4 w-4" />
          <span className="tabular font-mono font-semibold">{pedido.pedidoMarketplace}</span>
        </span>
        {pedido.rastreio ? (
          <span className="block text-[10.5px] text-textoSuave">rastreio {pedido.rastreio}</span>
        ) : null}
        {/* TikTok imprime por PACOTE e não tem envio no hub — "sem envio no
            hub" em vermelho ali apontava um problema que não existe. */}
        {pedido.canal === 'tiktok' ? (
          <span className="tabular ml-[22px] block font-mono text-[10.5px] text-textoSuave">
            {pedido.pacotes?.length
              ? `pacote ${pedido.pacotes.join(', ')}`
              : 'sem pacote no TikTok'}
          </span>
        ) : pedido.shipmentId ? (
          <span className="tabular ml-[22px] block font-mono text-[10.5px] text-textoSuave">
            envio {pedido.shipmentId}
          </span>
        ) : (
          <span className="ml-[22px] block text-[10.5px] text-status-divergencia">
            sem envio no hub
          </span>
        )}
      </td>

      <td className="tabular py-2 pr-2 font-mono font-semibold">{pedido.televenda ?? '—'}</td>

      <td className="py-2 pr-2">
        <span className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'inline-block rounded-md px-1.5 py-[3px] text-[11.5px] font-bold leading-none',
              CLASSE_TOM[status.tom],
            )}
          >
            {status.rotulo}
          </span>
          {status.etiqueta === 'sai' ? null : (
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-textoSuave">
              {status.etiqueta === 'nunca' ? 'não imprime' : 'ainda não'}
            </span>
          )}
          {!pedido.aoVivo ? (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] text-status-atencao"
              title={pedido.erroMl ?? 'O ML não respondeu; mostrando a foto do banco.'}
            >
              <WifiOff className="h-3 w-3" />
              foto do banco
            </span>
          ) : null}
        </span>

        <span className="mt-0.5 block text-[11.5px] text-textoFraco">{status.detalhe}</span>

        {/*
          A ação fica COLADA no motivo, não na ponta direita da linha.

          Na última coluna, em tamanho micro, ela desaparecia: o operador lê
          "aguardando nota fiscal", e a saída para isso precisa estar onde o
          olho já está — não a três colunas de distância.
        */}
        {status.codigo === 'invoice_pending' && pedido.televenda !== null ? (
          <Botao
            variante="primaria"
            tamanho="pequeno"
            className="mt-1.5"
            onClick={aoEnviarXml}
            carregando={enviandoXml}
            disabled={ocupado && !enviandoXml}
            title={`Envia a NF-e do pedido ${pedido.televenda} ao Mercado Livre para liberar a etiqueta`}
          >
            <Send className="h-4 w-4" />
            Enviar NF-e agora
          </Botao>
        ) : null}

        {pedido.retidoAte ? (
          <span className="mt-0.5 block text-[11px] text-status-atencao">
            O ML libera em {soData(pedido.retidoAte)}.
          </span>
        ) : null}

        {/* O código cru só aparece quando não há tradução — aí ele é a informação. */}
        {!status.conhecido && status.codigo ? (
          <span className="tabular mt-0.5 block font-mono text-[10.5px] text-textoSuave">
            código do ML: {status.codigo}
          </span>
        ) : null}
      </td>

      <td className="py-2 pr-2">
        {pedido.slaEm ? (
          <>
            <span className="tabular flex items-center gap-1 font-semibold">
              <Clock className="h-3 w-3 text-textoSuave" />
              {dataHora(pedido.slaEm)}
            </span>
            {pedido.slaStatus && pedido.slaStatus !== 'on_time' ? (
              <span className="block text-[10.5px] font-semibold text-status-divergencia">
                {pedido.slaStatus === 'delayed' ? 'atrasado' : pedido.slaStatus}
              </span>
            ) : null}
          </>
        ) : (
          <span className="tabular text-textoSuave">{pedido.agendamentoFoto ?? '—'}</span>
        )}
      </td>

      <td className="py-2 pr-2 text-[11.5px] text-textoFraco">{pedido.statusProcfit ?? '—'}</td>

      {/*
        Reimprimir UMA etiqueta.
        O caso é o da etiqueta que molhou no meio de um romaneio de 20: mandar
        o lote de novo gastaria 19 etiquetas boas e ainda obrigaria alguém a
        achar a certa dentro do PDF. Só aparece quando o ML libera aquela
        etiqueta — nas outras, o motivo já está na coluna ao lado.
      */}
      <td className="py-2 text-right">
        {status.etiqueta === 'sai' &&
        pedido.televenda !== null &&
        (pedido.shipmentId !== null || (pedido.pacotes?.length ?? 0) > 0) ? (
          <span className="inline-flex items-center justify-end gap-1">
            <Botao
              variante="secundaria"
              tamanho="micro"
              onClick={() => aoReimprimir('pdf')}
              carregando={reimprimindo}
              disabled={ocupado && !reimprimindo}
              title={`Reimprimir só a etiqueta do pedido ${pedido.televenda} (PDF)`}
            >
              <Printer className="h-3.5 w-3.5" />
              PDF
            </Botao>
            {/* ZPL é do Mercado Livre: o TikTok entrega a etiqueta em PDF, e
                um botão que devolve PDF com nome .zpl quebra a Zebra calado. */}
            {pedido.shipmentId !== null ? (
              <Botao
                variante="fantasma"
                tamanho="micro"
                onClick={() => aoReimprimir('zpl')}
                disabled={ocupado}
                title={`Baixar só a etiqueta do pedido ${pedido.televenda} em ZPL (.zip)`}
              >
                ZPL
              </Botao>
            ) : null}
          </span>
        ) : (
          <span className="text-[11px] text-textoSuave">—</span>
        )}
      </td>
    </tr>
  );
}
